import { ChatCompletion, ChatCompletionChunk, ChatCompletionMessage, ChatCompletionToolMessageParam } from "openai/resources";
import { CoverVisualDescriptionTerseResponse, StoryVisualDescriptionTerseResponse } from "../chatgpt/chatGptTemplates";
import { partialParse } from "../util/JsonParser";
import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { StoryStatus } from "./Enums";
import { StoryUI, storyUISchema } from "./StoryUI";
import { StoryImagePage, StoryPageEntry } from "./StoryBase";
import z from "zod";

export type MessageEntry = {
	storyStatus: StoryStatus;
	message: ChatCompletionMessage | ChatCompletionToolMessageParam;
};

export const storyDbSchema = storyUISchema.extend({
  _gptMessages: z.array(z.any()),
  _lastChatGptObject: z.any().optional(),
});

export type StoryDbFields = StripUnderscore<z.infer<typeof storyDbSchema>>;

export class StoryDb<TFields extends StoryDbFields = StoryDbFields> extends StoryUI<TFields> {
	_gptMessages: MessageEntry[] = [];
	_lastChatGptObject: ChatCompletion | ChatCompletionChunk | undefined;
	override _className: string = "Story";
	override _deserializeClassName : string  = "StoryDb";

	tempStorage: string = "";
	tempStorageToolCallId: string | undefined;
	tempStorageFunctionName: string | undefined;

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_gptMessages", "_lastChatGptObject"];
	}
	constructor(props?: Partial<TFields>) {
		super(props);
	}
	override validateSchema(): void {
		storyDbSchema.parse(this);
	}
	get gptMessages(): MessageEntry[] {
		return this.checkFieldType<"gptMessages">(this._gptMessages);
	}
	set gptMessages(value: MessageEntry[]) {
		this._gptMessages = this.checkFieldTypeAndMark<"gptMessages">("_gptMessages", value);
	}
	getGptMessages() {
		return this._gptMessages.map((msgEntry) => msgEntry.message);
	}
	get lastChatGptObject(): ChatCompletion | ChatCompletionChunk | undefined {
		return this.checkFieldType<"lastChatGptObject">(this._lastChatGptObject);
	}
	set lastChatGptObject(value: ChatCompletion | ChatCompletionChunk | undefined) {
		this._lastChatGptObject = this.checkFieldTypeAndMark<"lastChatGptObject">("_lastChatGptObject", value);
	}
	addGptAssistantMessage(storyStatus: StoryStatus, gptMessage: ChatCompletionMessage) {
		this._gptMessages.push({ storyStatus, message: gptMessage });
		this.markFieldDirty("_gptMessages");
	}
	addGptToolMessage(storyStatus: StoryStatus, gptMessage: ChatCompletionToolMessageParam) {
		this._gptMessages.push({ storyStatus, message: gptMessage });
		this.markFieldDirty("_gptMessages");
	}
	parseGptMessage(storyStatus: StoryStatus, obj: ChatCompletion | ChatCompletionChunk, streaming = false, storeMessages = true) {
		if (streaming) {
			const chatObject: ChatCompletionChunk = obj as ChatCompletionChunk;
			let response = {};
			if (chatObject.choices[0] && chatObject.choices[0].delta) {
				const delta = chatObject.choices[0].delta;
				if (delta.tool_calls && delta.tool_calls[0] && delta.tool_calls[0].function) {
					if (delta.tool_calls[0].function.arguments) this.tempStorage += delta.tool_calls[0].function.arguments;
					if (delta.tool_calls[0].id) this.tempStorageToolCallId = delta.tool_calls[0].id;
					if (delta.tool_calls[0].function.name) this.tempStorageFunctionName = delta.tool_calls[0].function.name;
				}
			}
			if (this.tempStorage !== undefined && this.tempStorage.length > 0) response = partialParse(this.tempStorage);
			if (chatObject.choices[0] && chatObject.choices[0].finish_reason === "stop" && this.tempStorageFunctionName && this.tempStorageToolCallId) {
				this.updateGptArtefactsStreaming(
					storyStatus,
					chatObject,
					this.tempStorage,
					this.tempStorageFunctionName,
					this.tempStorageToolCallId,
					storeMessages
				);
				this.tempStorage = "";
				this.tempStorageToolCallId = undefined;
				this.tempStorageFunctionName = undefined;
			} else {
				this.lastChatGptObject = chatObject;
			}
			return response;
		} else {
			const chatObject = obj as ChatCompletion;
			this.updateGptArtefacts(storyStatus, chatObject, storeMessages);
			const chatMessage = chatObject.choices[0]?.message;
			if (!chatMessage.tool_calls || !chatMessage.tool_calls[0]?.function.arguments)
				throw new BasicError("Response not present", "Response not present");
			return JSON.parse(chatMessage.tool_calls[0].function.arguments);
		}
	}
	updateGptArtefacts(storyStatus: StoryStatus, chatObject: ChatCompletion, storeMessages = true) {
		this.lastChatGptObject = chatObject;
		if (storeMessages) {
			const message = chatObject.choices[0]?.message;
			if (message) {
				this.addGptAssistantMessage(storyStatus, message);
				if (message.tool_calls) this.addGptToolMessage(storyStatus, { role: "tool", content: "", tool_call_id: message.tool_calls[0].id });
			}
		}
	}
	updateGptArtefactsStreaming(
		storyStatus: StoryStatus,
		chatObject: ChatCompletion | ChatCompletionChunk,
		finalContent: string,
		functionName: string,
		toolCallId: string,
		storeMessages: boolean = true
	) {
		this.lastChatGptObject = chatObject;
		if (storeMessages) {
			this.addGptAssistantMessage(storyStatus, {
				role: "assistant",
				refusal: null,
				content: null,
				tool_calls: [
					{
						id: toolCallId,
						type: "function",
						function: {
							name: functionName,
							arguments: finalContent,
						},
					},
				],
			});
			this.addGptToolMessage(storyStatus, { role: "tool", content: "", tool_call_id: toolCallId });
		}
	}
	override setStoryState(status: StoryStatus): void {
		const filteredMessages = this._gptMessages.filter((msgEntry) => msgEntry.storyStatus <= status);
		if (filteredMessages.length < this._gptMessages.length) {
			this._gptMessages = filteredMessages;
			this._lastChatGptObject = undefined;
			this.markFieldDirty("_gptMessages");
			this.markFieldDirty("_lastChatGptObject");
		}
		super.setStoryState(status);
	}
	setStoryTextFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		const pagesText: StoryPageEntry[] = this.parseGptMessage(StoryStatus.STORY_WRITTEN, chatObject, streaming).pages;
        if (!this.storyPages) this.storyPages = [];
		if (pagesText)
			pagesText.forEach((pageText: StoryPageEntry) => {
				if (this.storyPages) {
					this.storyPages[pageText.page - 1] = { textOptions: [pageText.text], textIndex: 0 };
				}
			});
		this.markFieldDirty("_storyPages");
		this.status = StoryStatus.STORY_WRITTEN;
	}
	setStoryRegenerateTextFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		const pageText: StoryPageEntry = this.parseGptMessage(StoryStatus.STORY_WRITTEN, chatObject, streaming);
		if (this.storyPages && pageText.page && pageText.text) {
			const storyPage = this.storyPages[pageText.page - 1];
			if (!storyPage) throw new BasicError("Page number invalid", "Not story page for pagenumber " + pageText.page);
			storyPage.textOptions[storyPage.textOptions.length - 1] = pageText.text;
		}
		this.markFieldDirty("_storyPages");
		this.setStoryState(StoryStatus.STORY_WRITTEN);
	}
	setTopicAnalysisFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		this.topicAnalysis = this.parseGptMessage(StoryStatus.TOPIC_ANALYSED, chatObject, streaming);
		this.status = StoryStatus.TOPIC_ANALYSED;
	}
	setTitleOptionsFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		this.titleOptions = this.parseGptMessage(StoryStatus.TITLE_OPTIONS_SET, chatObject, streaming).options;
		this.status = StoryStatus.TITLE_OPTIONS_SET;
	}
	setCharactersFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		this.characters = this.parseGptMessage(StoryStatus.CHARACTERS_IDENTIFIED, chatObject, streaming).characters;
		this.status = StoryStatus.CHARACTERS_IDENTIFIED;
	}
	setDedicationFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		this.dedication = this.parseGptMessage(StoryStatus.DEDICATION_ADDED, chatObject, streaming, false).dedication;
		this.status = StoryStatus.DEDICATION_ADDED;
	}
	setVisualDescriptionsFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		const visualDescriptions: StoryVisualDescriptionTerseResponse[] = this.parseGptMessage(
			StoryStatus.VISUAL_DESCRIPTIONS_PAGES,
			chatObject,
			streaming
		).visualDescriptions;
		if (!this.storyPages) throw new BasicError("Visual description error", "Cannot set visual descriptions as no story text");
		if (!this.storyImagePages) this.storyImagePages = new Array<StoryImagePage>(this.storyPages.length);
		if (visualDescriptions) {
			visualDescriptions.forEach((visualDesc) => {
				if (visualDesc.page) {
					const imagePage = this.storyImagePages![visualDesc.page - 1];
					if (!imagePage) {
						this.storyImagePages![visualDesc.page - 1] = { visualDescription: visualDesc };
					} else {
						this.storyImagePages![visualDesc.page - 1].visualDescription = visualDesc;
					}
				}
			});
		}
		this.markFieldDirty("_storyImagePages");
		this.status = StoryStatus.VISUAL_DESCRIPTIONS_PAGES;
	}
	setCoverVisualDescriptionsFromChat(chatObject: ChatCompletion | ChatCompletionChunk, streaming = false) {
		const coverVisualDescriptions: CoverVisualDescriptionTerseResponse = this.parseGptMessage(
			StoryStatus.VISUAL_DESCRIPTIONS_COVER,
			chatObject,
			streaming
		).coverVisualDescriptions;
		if (coverVisualDescriptions) {
			this._frontCover = { visualDescription: coverVisualDescriptions.frontCover };
			this._backCover = { visualDescription: coverVisualDescriptions.backCover };
			this.markFieldDirty("_frontCover");
			this.markFieldDirty("_backCover");
			this.status = StoryStatus.VISUAL_DESCRIPTIONS_COVER;
		}
	}
}
